<template>
  <header class="fixed top-0 left-0 right-0 z-50">
    <!-- ── Top Bar ─────────────────────────────────────────────── -->
    <div
      :class="[
        'bg-primary transition-all duration-500 ease-in-out overflow-hidden',
        scrolled ? 'max-h-0 opacity-0 py-0' : 'max-h-14 opacity-100 py-2.5',
      ]"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between gap-4">
          <!-- Contact info -->
          <div class="hidden md:flex items-center gap-6 text-white/85 text-xs">
            <span class="flex items-center gap-1.5">
              <i class="fa-solid fa-location-dot text-secondary text-[10px]"></i>
              Rubaga Road, Butikiro Complex
            </span>
            <a
              href="mailto:orelmedicalsupplies@gmail.com"
              class="flex items-center gap-1.5 hover:text-white transition-colors duration-200"
            >
              <i class="fa-solid fa-envelope text-secondary text-[10px]"></i>
              orelmedicalsupplies@gmail.com
            </a>
            <a
              href="tel:+256779266772"
              class="flex items-center gap-1.5 hover:text-white transition-colors duration-200"
            >
              <i class="fa-solid fa-phone text-secondary text-[10px]"></i>
              +256779266772/+256769571216
            </a>
          </div>
          <!-- Social links -->
          <div class="flex items-center gap-3 ml-auto md:ml-0">
            <a href="https://www.facebook.com/" target="_blank" rel="noopener"
              class="text-white/70 hover:text-secondary transition-colors duration-200 text-xs">
              <i class="fa-brands fa-facebook-f"></i>
            </a>
            <a href="https://www.linkedin.com/" target="_blank" rel="noopener"
              class="text-white/70 hover:text-secondary transition-colors duration-200 text-xs">
              <i class="fa-brands fa-linkedin-in"></i>
            </a>
            <a href="https://twitter.com/" target="_blank" rel="noopener"
              class="text-white/70 hover:text-secondary transition-colors duration-200 text-xs">
              <i class="fa-brands fa-twitter"></i>
            </a>
            <a href="https://www.instagram.com/" target="_blank" rel="noopener"
              class="text-white/70 hover:text-secondary transition-colors duration-200 text-xs">
              <i class="fa-brands fa-instagram"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Main Nav ──────────────────────────────────────────────── -->
    <nav
      :class="[
        'bg-white border-b border-gray-100 transition-all duration-300',
        scrolled ? 'shadow-lg py-3' : 'py-4',
      ]"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between gap-4">

          <!-- Logo -->
          <RouterLink to="/" class="flex items-center gap-3 flex-shrink-0 group">
            <div class="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300">
              <img
                src="/assets/images/logo/orel-logo-white.png"
                alt="Orel Medical Supplies"
                class="h-7 w-auto"
              />
            </div>
            <div class="hidden sm:block leading-tight">
              <div class="font-bold text-primary text-sm tracking-wide">OREL MEDICAL</div>
              <div class="text-gray-400 text-[10px] font-medium tracking-widest uppercase">Supplies Limited</div>
            </div>
          </RouterLink>

          <!-- Desktop Nav -->
          <div class="hidden lg:flex items-center gap-1">
            <RouterLink
              v-for="link in navLinks"
              :key="link.to"
              :to="link.to"
              :class="[
                'relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 group',
                isActive(link.to)
                  ? 'text-primary'
                  : 'text-gray-600 hover:text-primary',
              ]"
            >
              {{ link.label }}
              <!-- Active/hover underline -->
              <span
                :class="[
                  'absolute bottom-1 left-4 right-4 h-0.5 bg-primary rounded-full transition-all duration-300',
                  isActive(link.to) ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0 group-hover:opacity-60 group-hover:scale-x-100',
                ]"
              ></span>
            </RouterLink>
          </div>

          <!-- CTA + Mobile Toggle -->
          <div class="flex items-center gap-3">
            <RouterLink
              to="/contact-us"
              class="hidden lg:inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              <i class="fa-solid fa-paper-plane text-xs"></i>
              Get a Quote
            </RouterLink>
            <button
              @click="mobileOpen = true"
              class="lg:hidden p-2.5 text-gray-600 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-200"
              aria-label="Open menu"
            >
              <i class="fa-solid fa-bars text-xl"></i>
            </button>
          </div>

        </div>
      </div>
    </nav>
  </header>

  <!-- ── Mobile Menu Overlay ─────────────────────────────────────── -->
  <Teleport to="body">
    <Transition name="mobile-overlay">
      <div
        v-if="mobileOpen"
        class="fixed inset-0 z-[200] lg:hidden"
        role="dialog"
        aria-modal="true"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/60 backdrop-blur-sm"
          @click="mobileOpen = false"
        ></div>

        <!-- Drawer -->
        <Transition name="mobile-drawer">
          <div
            v-if="mobileOpen"
            class="absolute right-0 top-0 bottom-0 w-[300px] bg-white flex flex-col shadow-2xl"
          >
            <!-- Drawer header -->
            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-primary-darkest to-primary-dark">
              <RouterLink to="/" @click="mobileOpen = false" class="flex items-center gap-2.5">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                  <img src="/assets/images/logo/orel-logo-white.png" class="h-6 w-auto" alt="Logo" />
                </div>
                <div>
                  <div class="font-bold text-white text-sm">OREL MEDICAL</div>
                  <div class="text-white/50 text-[10px] uppercase tracking-widest">Supplies Ltd</div>
                </div>
              </RouterLink>
              <button
                @click="mobileOpen = false"
                class="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-200"
                aria-label="Close menu"
              >
                <i class="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            <!-- Nav links -->
            <nav class="flex-1 overflow-y-auto px-4 py-5">
              <RouterLink
                v-for="link in navLinks"
                :key="link.to"
                :to="link.to"
                @click="mobileOpen = false"
                :class="[
                  'flex items-center gap-3.5 px-4 py-3.5 rounded-xl mb-1 text-sm font-medium transition-all duration-200',
                  isActive(link.to)
                    ? 'text-white bg-gradient-primary shadow-md'
                    : 'text-gray-600 hover:text-primary hover:bg-primary/5',
                ]"
              >
                <i :class="['text-base w-5 text-center', link.icon, isActive(link.to) ? 'text-white' : 'text-primary/70']"></i>
                {{ link.label }}
                <i
                  v-if="isActive(link.to)"
                  class="fa-solid fa-chevron-right text-xs ml-auto text-white/70"
                ></i>
              </RouterLink>

              <div class="mt-4 pt-4 border-t border-gray-100">
                <RouterLink
                  to="/contact-us"
                  @click="mobileOpen = false"
                  class="flex items-center justify-center gap-2 w-full bg-primary text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-md"
                >
                  <i class="fa-solid fa-paper-plane text-xs"></i>
                  Get a Quote
                </RouterLink>
              </div>
            </nav>

            <!-- Contact info at bottom -->
            <div class="px-5 pb-6 pt-4 border-t border-gray-100">
              <div class="space-y-3">
                <a
                  href="tel:+256779266772"
                  class="flex items-center gap-3 text-sm text-gray-600 hover:text-primary transition-colors duration-200"
                >
                  <div class="w-8 h-8 bg-primary/8 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-phone text-primary text-xs"></i>
                  </div>
                  +256779266772
                </a>
                <a
                  href="mailto:orelmedicalsupplies@gmail.com"
                  class="flex items-center gap-3 text-sm text-gray-600 hover:text-primary transition-colors duration-200"
                >
                  <div class="w-8 h-8 bg-primary/8 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-envelope text-primary text-xs"></i>
                  </div>
                  <span class="truncate">orelmedicalsupplies@gmail.com</span>
                </a>
              </div>
              <!-- Social -->
              <div class="flex gap-2.5 mt-4">
                <a v-for="s in socials" :key="s.href" :href="s.href" target="_blank" rel="noopener"
                  class="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white hover:bg-primary-dark transition-colors duration-200">
                  <i :class="['text-xs', s.icon]"></i>
                </a>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

const route      = useRoute()
const scrolled   = ref(false)
const mobileOpen = ref(false)

const navLinks = [
  { to: '/',            label: 'Home',        icon: 'fa-solid fa-house' },
  { to: '/about-us',    label: 'About Us',    icon: 'fa-solid fa-circle-info' },
  { to: '/services',    label: 'Our Services',icon: 'fa-solid fa-stethoscope' },
  { to: '/our-products',label: 'Our Products',icon: 'fa-solid fa-boxes-stacked' },
  { to: '/contact-us',  label: 'Contact Us',  icon: 'fa-solid fa-envelope' },
  { to: '/faqs',        label: 'FAQs',        icon: 'fa-solid fa-circle-question' },
]

const socials = [
  { href: 'https://www.facebook.com/', icon: 'fa-brands fa-facebook-f' },
  { href: 'https://www.linkedin.com/', icon: 'fa-brands fa-linkedin-in' },
  { href: 'https://twitter.com/',      icon: 'fa-brands fa-twitter' },
  { href: 'https://www.instagram.com/',icon: 'fa-brands fa-instagram' },
]

const isActive = (path) => {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

const handleScroll = () => { scrolled.value = window.scrollY > 56 }

onMounted(()  => window.addEventListener('scroll', handleScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', handleScroll))
</script>
